/* oxlint-disable typescript/no-explicit-any -- error types need flexible any for dynamic issue data */
/* oxlint-disable typescript/no-unsafe-argument, typescript/no-unsafe-assignment, typescript/no-unsafe-call, typescript/no-unsafe-member-access -- schema library uses any types throughout */
import * as util from './util'

// Issue types
export type IssueCode =
  | 'invalid_key'
  | 'invalid_value'
  | 'invalid_union'
  | 'unrecognized_keys'
  | 'invalid_type'
  | 'invalid_literal'
  | 'invalid_enum_value'
  | 'invalid_string'
  | 'invalid_format'
  | 'invalid_arguments'
  | 'too_small'
  | 'too_big'
  | 'custom'

export interface BaseIssue {
  code: IssueCode
  message: string
  path?: ReadonlyArray<PropertyKey>
  input?: unknown
}

export interface InvalidTypeIssue extends BaseIssue {
  code: 'invalid_type'
  expected: string
  received?: string
}

export interface InvalidLiteralIssue extends BaseIssue {
  code: 'invalid_literal'
  expected: util.Literal
}

export interface InvalidEnumValueIssue extends BaseIssue {
  code: 'invalid_enum_value'
  options: readonly any[]
}

export interface InvalidStringIssue extends BaseIssue {
  code: 'invalid_string'
  validation: 'email' | 'url' | 'regex' | 'startsWith' | 'endsWith' | 'includes'
  pattern?: string
}

export interface InvalidFormatIssue extends BaseIssue {
  code: 'invalid_format'
  format: string
}

export interface TooSmallIssue extends BaseIssue {
  code: 'too_small'
  minimum: number | bigint
  inclusive: boolean
  origin: 'string' | 'number' | 'array' | 'date' | 'int' | 'bigint'
}

export interface TooBigIssue extends BaseIssue {
  code: 'too_big'
  maximum: number | bigint
  inclusive: boolean
  origin: 'string' | 'number' | 'array' | 'date' | 'int' | 'bigint'
}

export interface CustomIssue extends BaseIssue {
  code: 'custom'
}

export interface InvalidValueIssue extends BaseIssue {
  code: 'invalid_value'
  values: readonly util.Literal[]
}

export interface InvalidUnionIssue extends BaseIssue {
  code: 'invalid_union'
  errors: Issue[][]
  note?: string
  discriminator?: string
  options?: unknown[]
}

export interface InvalidKeyIssue extends BaseIssue {
  code: 'invalid_key'
  origin: 'record'
  issues: Issue[]
}

export interface UnrecognizedKeysIssue extends BaseIssue {
  code: 'unrecognized_keys'
  keys: string[]
}

export type Issue =
  | InvalidValueIssue
  | InvalidUnionIssue
  | InvalidKeyIssue
  | UnrecognizedKeysIssue
  | InvalidTypeIssue
  | InvalidLiteralIssue
  | InvalidEnumValueIssue
  | InvalidStringIssue
  | InvalidFormatIssue
  | TooSmallIssue
  | TooBigIssue
  | CustomIssue

// Internal raw issue (before message formatting)
export interface RawIssue {
  code: IssueCode
  path?: PropertyKey[]
  input?: unknown
  inst?: any
  continue?: boolean
  [key: string]: any
}

// Error map for customizing messages
export type ErrorMap = (
  issue: RawIssue,
  ctx: { defaultError: string },
) => { message: string }

// Default error messages
// Adapted from pinned Zod 4.5.4 locales/en.ts and util.finalizeIssue.
export const defaultErrorMap: ErrorMap = (issue) => {
  const literal = (value: unknown) => typeof value === 'bigint' ? value.toString() + 'n' : JSON.stringify(value)
  let message = 'Invalid input'
  switch (issue.code) {
    case 'invalid_type': {
      const input = issue.input
      const received = input === null ? 'null' : Array.isArray(input) ? 'array' : input instanceof Date ? 'date' : typeof input === 'number' && !Number.isFinite(input) ? String(input) : typeof input
      message = `Invalid input: expected ${issue.expected}, received ${received}`
      break
    }
    case 'invalid_value':
      message = issue.values.length === 1 ? `Invalid input: expected ${literal(issue.values[0])}` : `Invalid option: expected one of ${issue.values.map(literal).join('|')}`
      break
    case 'too_small': case 'too_big': {
      const small = issue.code === 'too_small'
      const adj = issue.exact ? 'exactly ' : small ? (issue.inclusive ? '>=' : '>') : (issue.inclusive ? '<=' : '<')
      const bound = small ? issue.minimum : issue.maximum
      const units = issue.origin === 'string' ? 'characters' : issue.origin === 'array' ? 'items' : undefined
      message = `Too ${small ? 'small' : 'big'}: expected ${issue.origin} to ${units ? 'have' : 'be'} ${adj}${bound}${units ? ' ' + units : ''}`
      break
    }
    case 'invalid_format':
      if (issue.format === 'starts_with') message = `Invalid string: must start with "${issue.prefix}"`
      else if (issue.format === 'ends_with') message = `Invalid string: must end with "${issue.suffix}"`
      else if (issue.format === 'includes') message = `Invalid string: must include "${issue.includes}"`
      else if (issue.format === 'regex') message = `Invalid string: must match pattern ${issue.pattern}`
      else message = `Invalid ${issue.format === 'email' ? 'email address' : issue.format === 'url' ? 'URL' : issue.format}`
      break
    case 'unrecognized_keys':
      message = `Unrecognized key${issue.keys.length > 1 ? 's' : ''}: ${issue.keys.map(literal).join(', ')}`
      break
    case 'invalid_key': message = `Invalid key in ${issue.origin}`; break
    case 'invalid_union':
      if (issue.options?.length > 0) message = `Invalid discriminator value. Expected ${issue.options.map((v: unknown) => "'" + String(v) + "'").join(' | ')}`
      break
    case 'custom': case 'invalid_arguments': case 'invalid_enum_value': case 'invalid_literal': case 'invalid_string': break
  }
  return { message }
}

export function formatIssue(raw: RawIssue, errorMap?: ErrorMap): Issue {
  const issue = { ...raw }
  if (issue.code === 'invalid_literal' || issue.code === 'invalid_enum_value') {
    issue.values = issue.code === 'invalid_literal' ? [issue.expected] : issue.options
    issue.code = 'invalid_value'
    delete issue.expected
    delete issue.options
  }
  if (issue.code === 'too_small' || issue.code === 'too_big') {
    issue.origin = issue.origin ?? issue.type
    delete issue.type
  }
  if (issue.code === 'invalid_string') {
    issue.code = 'invalid_format'
    issue.origin = 'string'
    issue.format = issue.validation === 'startsWith' ? 'starts_with' : issue.validation === 'endsWith' ? 'ends_with' : issue.validation
    if (issue.validation === 'startsWith') issue.prefix = issue.value
    if (issue.validation === 'endsWith') issue.suffix = issue.value
    if (issue.validation === 'includes') issue.includes = issue.value
    delete issue.validation
    delete issue.value
  }
  if (issue.code === 'invalid_format') issue.origin ??= 'string'
  const schemaMessage = issue.inst?._zod?.def?.error
  const message = issue.message ?? (typeof schemaMessage === 'string' ? schemaMessage : typeof schemaMessage === 'function' && schemaMessage.length < 2 ? schemaMessage() : undefined) ?? errorMap?.(issue, { defaultError: defaultErrorMap(issue, { defaultError: 'Invalid input' }).message }).message ?? defaultErrorMap(issue, { defaultError: 'Invalid input' }).message
  const { inst: _inst, continue: _continue, input: _input, ...rest } = issue
  return { ...rest, path: rest.path ?? [], message } as Issue
}

// SchemaError class
export class SchemaError extends Error {
  issues: Issue[]

  constructor(issues: RawIssue[], errorMap?: ErrorMap) {
    const formatted = issues.map((i) => formatIssue(i, errorMap))
    super(JSON.stringify(formatted, util.jsonStringifyReplacer, 2))
    this.name = 'SchemaError'
    this.issues = formatted
  }
}
